import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, merge } from 'rxjs';
import {
    filter,
    map,
    switchMap,
    tap,
    withLatestFrom,
    distinct,
    distinctUntilChanged,
} from 'rxjs/operators';
import { IAclID } from 'src/app/acl-data/aclID.interface';
import { FolderService } from 'src/app/acl-data/folder/folder.service';
import { IBookmarkEntry } from 'src/app/navigation/menuInterfaces/bookmark-entry.interface';
import { TopnavInfoService } from 'src/app/navigation/menuInterfaces/topnav-info.service';
import { SubSink } from 'subsink';

@Component({
    templateUrl: './browse.component.html',
    styleUrls: ['./browse.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrowseComponent implements OnDestroy {
    private subSink = new SubSink();
    folderID$ = this.route.paramMap.pipe(
        map(v => v.get('folderID')),
        withLatestFrom(this.route.data),

        map(([idValue, routeValue]) => {
            return {
                id: idValue,
                objectStore: routeValue.parentMenu.data.id,
            } as IAclID;
        })
    );

    folder$ = this.folderID$.pipe(
        switchMap(v => this.folderService.getByID(v.objectStore, v.id))
    );

    ngOnDestroy(): void {
        this.subSink.unsubscribe();
    }

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private folderService: FolderService,
        private topNavInfoService: TopnavInfoService
    ) {
        this.subSink.add(
            combineLatest([this.route.paramMap, this.folderID$])
                .pipe(
                    // tap(v => console.log('Pre Browse', v)),
                    filter(([parms, _]) => !parms.get('folderID')),
                    switchMap(([parms, folderID]) =>
                        this.folderService
                            .getRootFolder(folderID.objectStore)
                            .pipe(
                                filter(v => !!v),
                                map(rootFolder => rootFolder.id)
                            )
                    )
                )
                .subscribe(v => {
                    // console.log('Navigating: Browse', v);
                    this.router.navigate([v], {
                        relativeTo: this.route,
                        queryParamsHandling: 'merge',
                    });
                })
        );

        this.subSink.add(
            combineLatest([
                this.route.paramMap,
                this.route.data,
                this.folderService.entities$,
            ])
                .pipe(
                    filter(([parmMap, _]) => !!parmMap.get('folderID')),
                    tap(([parmMap, routeValue, entities]) => {
                        const objectStore = routeValue.parentMenu.data.id;
                        const idValue = parmMap.get('folderID');
                        const currentFolder = entities.find(
                            v => v.id === idValue
                        );
                        if (!currentFolder) {
                            return;
                        }
                        const result: IBookmarkEntry[] = [];

                        if (currentFolder.parentID) {
                            const currentFolderParent = entities.find(
                                v => v.id === currentFolder.parentID
                            );

                            if (
                                currentFolderParent &&
                                currentFolderParent.parentID
                            ) {
                                //Support 3 levels
                                const currentFolderGrandParent = entities.find(
                                    v => v.id === currentFolderParent.parentID
                                );

                                if (
                                    currentFolderGrandParent &&
                                    !currentFolderGrandParent.isRoot
                                ) {
                                    result.push({
                                        path: `/${objectStore}/browse/${currentFolderGrandParent.id}`,
                                        description:
                                            currentFolderGrandParent
                                                .propertiesObj.FolderName,
                                    });
                                }
                            }

                            if (
                                currentFolderParent &&
                                !currentFolderParent.isRoot
                            ) {
                                result.push({
                                    path: `/${objectStore}/browse/${currentFolderParent.id}`,
                                    description:
                                        currentFolderParent.propertiesObj
                                            .FolderName,
                                });
                            }
                        }

                        result.push({
                            path: `/${objectStore}/browse/${currentFolder.id}`,
                            description: currentFolder.propertiesObj.FolderName,
                        });

                        this.topNavInfoService.addExtraBreadCrumbData(result);
                    })
                )
                .subscribe(v => {
                    return;
                })
        );

        // this.subSink.add(
        //     combineLatest([
        //         this.route.paramMap,
        //         this.route.data,
        //         this.folderService.entities$,
        //     ])
        //         .pipe(
        //             filter(([parmMap, _]) => !!parmMap.get('folderID')),
        //             tap(([parmMap, routeValue, entities]) => {
        //                 const objectStore = routeValue.parentMenu.data.id;
        //                 const idValue = parmMap.get('folderID');
        //                 const currentFolder = entities.find(
        //                     v => v.id === idValue
        //                 );
        //                 if (!currentFolder) {
        //                     return;
        //                 }
        //                 const result: IBookmarkEntry[] = [];

        //                 if (currentFolder.parentID) {
        //                     const currentFolderParent = entities.find(
        //                         v => v.id === currentFolder.parentID
        //                     );
        //                     if (
        //                         currentFolderParent &&
        //                         !currentFolderParent.isRoot
        //                     ) {
        //                         result.push({
        //                             path: `/${objectStore}/browse/${currentFolderParent.id}`,
        //                             description:
        //                                 currentFolderParent.propertiesObj
        //                                     .FolderName,
        //                         });
        //                     }
        //                 }

        //                 result.push({
        //                     path: `/${objectStore}/browse/${currentFolder.id}`,
        //                     description: currentFolder.propertiesObj.FolderName,
        //                 });

        //                 this.topNavInfoService.addExtraBreadCrumbData(result);
        //             })
        //         )
        //         .subscribe(v => {
        //             return;
        //         })
        // );
    }
}
